import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Image as ImageIcon, FileUp, Plus, Trash2, Loader2, CheckCircle2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, errCode, fmtBytes } from "@/lib/api";
import { useApp } from "@/lib/app-context";
import { CURRENCIES } from "@/lib/geo";
import { PricingCards } from "@/components/ww/Pricing";
import { Spinner } from "@/components/ww/Misc";
import { ScanCard } from "@/components/ww/ScanCard";

export const CATEGORIES = [["invoice", "Purchase Invoice"], ["warranty_card", "Warranty Card"], ["guarantee_card", "Guarantee Card"], ["other", "Other Supporting Document"]];
export const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";
const OK_EXT = ["jpg", "jpeg", "png", "webp", "pdf"];
const today = () => new Date().toLocaleDateString("en-CA");
const blankCov = () => ({ key: crypto.randomUUID(), type: "warranty", name: "", start_date: "", expiry_date: "", notes: "" });

export const checkFile = (f) => {
  const ext = f.name.split(".").pop().toLowerCase();
  if (!OK_EXT.includes(ext)) return "Only JPG, JPEG, PNG, WEBP and PDF files are accepted";
  if (f.size > 10 * 1024 * 1024) return `${f.name} is larger than 10 MB`;
  return null;
};

const Field = ({ label, required, children, hint }) => (
  <label className="block"><span className="mb-1.5 block text-sm font-semibold text-ww-navy">{label}{required && <span className="text-ww-emerald"> *</span>}</span>{children}{hint && <span className="mt-1 block text-xs text-ww-muted">{hint}</span>}</label>
);
const Card = ({ title, desc, children, testid }) => (
  <section className="rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid={testid}>
    <h2 className="font-display text-xl font-semibold text-ww-navy">{title}</h2>
    {desc && <p className="mt-1 text-sm text-ww-muted">{desc}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

function CoverageRow({ c, i, onChange, onRemove, canRemove }) {
  const set = (k) => (e) => onChange({ ...c, [k]: e?.target ? e.target.value : e });
  return (
    <div className="rounded-2xl border border-ww-border bg-ww-cyan/40 p-5" data-testid={`coverage-row-${i}`}>
      <div className="mb-4 flex items-center justify-between"><p className="text-sm font-bold uppercase tracking-wider text-ww-emerald">Coverage {i + 1}</p>
        {canRemove && <button type="button" onClick={onRemove} className="rounded-lg p-1.5 text-ww-muted hover:bg-red-50 hover:text-red-600" aria-label="Remove coverage" data-testid={`coverage-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}</div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Coverage Type" required>
          <Select value={c.type} onValueChange={set("type")}><SelectTrigger className="h-11 rounded-xl bg-white" data-testid={`coverage-type-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="warranty">Warranty</SelectItem><SelectItem value="guarantee">Guarantee</SelectItem></SelectContent></Select>
        </Field>
        <Field label="Coverage Name"><input className="ww-input" value={c.name} onChange={set("name")} placeholder="e.g. Compressor Warranty" maxLength={120} data-testid={`coverage-name-${i}`} /></Field>
        <Field label="Start Date" required><input type="date" className="ww-input" value={c.start_date} onChange={set("start_date")} data-testid={`coverage-start-${i}`} /></Field>
        <Field label="Expiry Date" required><input type="date" className="ww-input" value={c.expiry_date} min={c.start_date || undefined} onChange={set("expiry_date")} data-testid={`coverage-expiry-${i}`} /></Field>
      </div>
      <div className="mt-4"><Field label="Notes"><input className="ww-input" value={c.notes} onChange={set("notes")} maxLength={1000} placeholder="Optional" data-testid={`coverage-notes-${i}`} /></Field></div>
    </div>
  );
}

function DocPicker({ files, setFiles }) {
  const refs = { camera: useRef(), gallery: useRef(), file: useRef() };
  const add = (list) => {
    const next = [...files];
    for (const f of list) {
      const err = checkFile(f);
      if (err) { toast.error(err); continue; }
      if (next.length >= 10) { toast.error("Maximum 10 documents per product"); break; }
      next.push({ key: crypto.randomUUID(), file: f, category: "invoice", url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null });
    }
    setFiles(next);
  };
  const btn = "flex flex-1 items-center justify-center gap-2 rounded-2xl border border-dashed border-ww-emerald/40 bg-ww-mint/50 px-4 py-5 text-sm font-semibold text-ww-emerald transition-colors hover:bg-ww-mint";
  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="button" className={btn} onClick={() => refs.camera.current.click()} data-testid="doc-take-photo-button"><Camera className="h-5 w-5" />Take Photo</button>
        <button type="button" className={btn} onClick={() => refs.gallery.current.click()} data-testid="doc-gallery-button"><ImageIcon className="h-5 w-5" />Choose from Gallery</button>
        <button type="button" className={btn} onClick={() => refs.file.current.click()} data-testid="doc-upload-file-button"><FileUp className="h-5 w-5" />Upload File</button>
      </div>
      <input ref={refs.camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="doc-camera-input" />
      <input ref={refs.gallery} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="doc-gallery-input" />
      <input ref={refs.file} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="doc-file-input" />
      <p className="mt-2 text-xs text-ww-muted">JPG, JPEG, PNG, WEBP or PDF · up to 10 MB each · maximum 10 documents. Documents don't use extra product slots.</p>
      <div className="mt-4 space-y-3">
        {files.map((f, i) => (
          <div key={f.key} className="flex flex-col gap-3 rounded-2xl border border-ww-border p-3 sm:flex-row sm:items-center" data-testid={`pending-doc-${i}`}>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {f.url ? <img src={f.url} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ww-cyan text-ww-emerald"><FileText className="h-5 w-5" /></span>}
              <div className="min-w-0"><p className="truncate text-sm font-semibold text-ww-navy">{f.file.name}</p><p className="text-xs text-ww-muted">{fmtBytes(f.file.size)}</p></div>
            </div>
            <Select value={f.category} onValueChange={(v) => setFiles(files.map((x) => (x.key === f.key ? { ...x, category: v } : x)))}>
              <SelectTrigger className="h-10 rounded-xl sm:w-56" data-testid={`pending-doc-category-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <button type="button" onClick={() => setFiles(files.filter((x) => x.key !== f.key))} className="self-end rounded-lg p-2 text-ww-muted hover:bg-red-50 hover:text-red-600 sm:self-auto" aria-label="Remove file" data-testid={`pending-doc-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProductForm() {
  const { id, ownerId } = useParams();
  const editing = !!id;
  const base = ownerId ? `/family/shared/${ownerId}` : "";
  const detailPath = (pid) => (ownerId ? `/shared/${ownerId}/products/${pid}` : `/products/${pid}`);
  const walletPath = ownerId ? `/shared/${ownerId}` : "/wallet";
  const api = useApi();
  const navigate = useNavigate();
  const { me, refreshMe } = useApp();
  const empty = { name: "", brand: "", purchase_date: "", model_number: "", serial_number: "", purchase_price: "", price_currency: me?.currency || "INR", seller: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [covs, setCovs] = useState([blankCov()]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editing);
  const [saved, setSaved] = useState(null);
  const [limit, setLimit] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const reqId = useRef(crypto.randomUUID());

  useEffect(() => {
    if (!ownerId || editing) return;
    api("get", "/family").then((r) => {
      const l = r.shared_with_me.find((x) => x.owner_id === ownerId);
      if (!l?.can_edit) { toast.error("You have view-only access to this wallet", { id: "view-only" }); navigate(l ? walletPath : "/wallet"); }
      else setOwnerName(l.owner_name);
    }).catch(() => navigate("/wallet"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);
  useEffect(() => {
    if (!editing) return;
    api("get", `${base}/products/${id}`).then((p) => {
      if (ownerId && !p.can_edit) { toast.error("You have view-only access to this wallet", { id: "view-only" }); navigate(detailPath(id)); return; }
      if (ownerId) setOwnerName(p.owner_name);
      setForm({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, p[k] ?? ""])), price_currency: p.price_currency || me?.currency || "INR" });
      setCovs(p.coverages.map((c) => ({ ...c, key: c.id })));
      setLoaded(true);
    }).catch(() => { toast.error("Product not found"); navigate(walletPath); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const applyScan = (r, file) => {
    const pick = (cur, v) => (cur !== "" && cur != null ? cur : v ?? "");
    setForm((f) => ({
      ...f, name: pick(f.name, r.product_name), brand: pick(f.brand, r.brand), model_number: pick(f.model_number, r.model_number),
      serial_number: pick(f.serial_number, r.serial_number), seller: pick(f.seller, r.seller),
      purchase_date: pick(f.purchase_date, r.purchase_date && r.purchase_date <= today() ? r.purchase_date : ""),
      purchase_price: pick(f.purchase_price, r.purchase_price), price_currency: r.price_currency && CURRENCIES.includes(r.price_currency) && f.purchase_price === "" ? r.price_currency : f.price_currency,
    }));
    const scanned = (r.coverages || []).map((c) => ({ ...blankCov(), type: c.type, name: c.name || "", start_date: c.start_date || "", expiry_date: c.expiry_date || "" }));
    if (scanned.length) setCovs((cs) => [...cs.filter((c) => c.start_date || c.expiry_date || c.name), ...scanned].slice(0, 20));
    setFiles((fs) => (fs.length >= 10 || fs.some((x) => x.file === file) ? fs : [...fs, { key: crypto.randomUUID(), file, category: "warranty_card", url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null }]));
  };
  const validate = () => {
    if (!form.name.trim() || !form.brand.trim() || !form.purchase_date) return "Please fill Product Name, Brand and Purchase Date";
    if (form.purchase_date > today()) return "Purchase Date cannot be in the future";
    if (!covs.length) return "Add at least one coverage period";
    for (const [i, c] of covs.entries()) {
      if (!c.start_date || !c.expiry_date) return `Coverage ${i + 1}: Start and Expiry dates are required`;
      if (c.expiry_date < c.start_date) return `Coverage ${i + 1}: Expiry Date cannot be earlier than Start Date`;
    }
    return null;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    const payload = { ...form, purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
      coverages: covs.map(({ key, status, days_left, ...c }) => c), client_request_id: editing ? undefined : reqId.current };
    try {
      const p = editing ? await api("put", `${base}/products/${id}`, payload) : await api("post", `${base}/products`, payload);
      let failed = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        fd.append("category", f.category);
        try { await api("post", `${base}/products/${p.id}/documents`, fd); } catch (ue) { failed += 1; toast.error(`${f.file.name}: ${errMsg(ue)}`); }
      }
      refreshMe();
      if (editing) { toast.success("Product updated"); navigate(detailPath(p.id)); }
      else setSaved({ ...p, failed });
    } catch (ex) {
      if (errCode(ex) === "limit_reached" && ownerId) toast.error("This wallet has reached its plan limit. Ask the wallet owner to upgrade.");
      else if (errCode(ex) === "limit_reached") setLimit(true);
      else toast.error(errMsg(ex));
    } finally { setBusy(false); }
  };
  const reset = () => { setForm(empty); setCovs([blankCov()]); setFiles([]); setSaved(null); reqId.current = crypto.randomUUID(); window.scrollTo(0, 0); };

  if (!loaded) return <Spinner />;
  return (
    <main className="mx-auto max-w-4xl px-5 py-12 lg:px-8" data-testid="product-form-page">
      <h1 className="font-display text-4xl font-semibold tracking-tight text-ww-navy sm:text-5xl">{editing ? "Edit Product" : "Add New Product"}</h1>
      <p className="mt-2 text-ww-muted" data-testid="product-form-subtitle">{ownerId ? "Saving to a household wallet shared with you. The owner is notified of what you add or change." : "Save a purchase and its warranty or guarantee coverage."}</p>
      <form onSubmit={submit} className="mt-10 space-y-6" noValidate>
        {!editing && <ScanCard onResult={applyScan} />}
        <Card title="Product Information" testid="product-info-section">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Product Name" required><input className="ww-input" value={form.name} onChange={set("name")} maxLength={120} placeholder="e.g. Samsung Refrigerator" data-testid="product-name-input" /></Field>
            <Field label="Brand" required><input className="ww-input" value={form.brand} onChange={set("brand")} maxLength={80} placeholder="e.g. Samsung" data-testid="product-brand-input" /></Field>
            <Field label="Purchase Date" required><input type="date" className="ww-input" value={form.purchase_date} max={today()} onChange={set("purchase_date")} data-testid="product-purchase-date-input" /></Field>
            <Field label="Model Number"><input className="ww-input" value={form.model_number} onChange={set("model_number")} maxLength={80} data-testid="product-model-input" /></Field>
            <Field label="Serial Number"><input className="ww-input" value={form.serial_number} onChange={set("serial_number")} maxLength={80} data-testid="product-serial-input" /></Field>
            <Field label="Purchase Price">
              <div className="flex gap-2">
                <Select value={form.price_currency} onValueChange={set("price_currency")}><SelectTrigger className="h-11 w-28 rounded-xl" data-testid="product-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                <input type="number" min="0" step="0.01" inputMode="decimal" className="ww-input flex-1" value={form.purchase_price} onChange={set("purchase_price")} data-testid="product-price-input" />
              </div>
            </Field>
            <Field label="Store / Seller Name"><input className="ww-input" value={form.seller} onChange={set("seller")} maxLength={120} data-testid="product-seller-input" /></Field>
          </div>
          <div className="mt-5"><Field label="Notes"><textarea className="ww-input min-h-[96px] py-3" value={form.notes} onChange={set("notes")} maxLength={2000} data-testid="product-notes-input" /></Field></div>
        </Card>

        {!editing && <Card title="Documents" desc="Upload your invoice, warranty or guarantee card." testid="documents-section"><DocPicker files={files} setFiles={setFiles} /></Card>}
        {editing && <p className="rounded-2xl bg-ww-cyan px-5 py-4 text-sm text-ww-navy/80">{ownerId ? "More documents can be added on the product details page." : "Documents for this product can be added, replaced or deleted on the product details page."}</p>}

        <Card title="Coverage Periods" desc="Add all warranties and guarantees that apply to this product." testid="coverage-section">
          <div className="space-y-4">
            {covs.map((c, i) => <CoverageRow key={c.key} c={c} i={i} canRemove={covs.length > 1} onChange={(v) => setCovs(covs.map((x) => (x.key === c.key ? v : x)))} onRemove={() => setCovs(covs.filter((x) => x.key !== c.key))} />)}
          </div>
          {covs.length < 20 && <button type="button" onClick={() => setCovs([...covs, blankCov()])} className="ww-btn-ghost mt-4" data-testid="add-another-coverage-button"><Plus className="h-4 w-4" />Add Another Coverage</button>}
        </Card>

        <button type="submit" disabled={busy} className="ww-btn-primary ww-btn-lg w-full justify-center" data-testid="save-product-button">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : editing ? "Save Changes" : ownerId ? `Save Product to ${ownerName || "Household"}'s Wallet` : "Save Product to My Wallet"}
        </button>
      </form>

      <Dialog open={!!saved} onOpenChange={(o) => !o && navigate(walletPath)}>
        <DialogContent className="max-w-md rounded-[28px] p-8 text-center" data-testid="product-saved-dialog">
          <CheckCircle2 className="mx-auto h-14 w-14 text-ww-emerald" />
          <DialogTitle className="font-display text-2xl text-ww-navy">Product Saved Successfully!</DialogTitle>
          <DialogDescription>{saved?.failed ? `${saved.failed} document(s) could not be uploaded. You can add them from the product page.` : "Reminders have been scheduled for each coverage period."}</DialogDescription>
          <div className="mt-4 grid gap-3">
            <button className="ww-btn-primary justify-center" onClick={() => navigate(detailPath(saved.id))} data-testid="saved-view-product-button">View Product</button>
            <button className="ww-btn-ghost justify-center" onClick={reset} data-testid="saved-add-another-button">Add Another Product</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={limit} onOpenChange={setLimit}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-[28px] p-6 sm:p-8" data-testid="upgrade-dialog">
          <DialogTitle className="font-display text-2xl text-ww-navy">You've reached your plan limit</DialogTitle>
          <DialogDescription>Your {me?.plan_name} plan allows {me?.limit} product records. Upgrade for more storage — your existing records stay safe.</DialogDescription>
          <PricingCards onPaid={() => setLimit(false)} />
        </DialogContent>
      </Dialog>
    </main>
  );
}
